import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button } from './Button';

// Must match the installed pdfjs-dist version (react-pdf's dependency) —
// react-pdf refuses to render if the worker version doesn't match exactly.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface InvoicePreviewProps {
  src: string;
  alt?: string;
  className?: string;
  /** PDF page width in px — size this to the caller's own container. */
  pageWidth?: number;
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  src,
  alt,
  className,
  pageWidth = 560,
}) => {
  const isPdf = src.split('?')[0].toLowerCase().endsWith('.pdf');
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  if (!isPdf) {
    return <img src={src} alt={alt} className={className} />;
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <Document
        file={src}
        onLoadSuccess={({ numPages: total }) => {
          setNumPages(total);
          setPageNumber(1);
        }}
        loading={
          <div className="flex h-[400px] w-full items-center justify-center text-[#B3B3B2]">
            Loading invoice…
          </div>
        }
        error={
          <div className="flex h-[400px] w-full items-center justify-center text-[#B3B3B2]">
            Couldn&apos;t load this invoice
          </div>
        }
        className={className}
      >
        <Page
          pageNumber={pageNumber}
          width={pageWidth}
          renderAnnotationLayer={false}
        />
      </Document>
      {numPages > 1 && (
        <div className="flex items-center gap-3">
          <Button
            variant="outlined"
            color="default"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-[#B3B3B2]">
            Page {pageNumber} of {numPages}
          </span>
          <Button
            variant="outlined"
            color="default"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default InvoicePreview;
